import formal_document_template_data_table_editor_component from "@/components/probaworkflows_base_components/FormalDocumentTemplateLibrary/FormalDocumentTemplateDataTableEditor.vue";
import {
  build_checklist_row,
  build_field_item,
  build_placeholder_label_from_binding,
  build_section_form,
  build_signatory,
  find_question_option,
  sync_binding_source,
} from "./formal_document_template_helpers";
import { ensure_formal_document_binding } from "./formal_document_template_binding_helpers";
import { get_bound_question_ids } from "./formal_document_template_selection_helpers";
import {
  PDF_SIGNATORY_TYPE_ADMIN,
  PDF_SIGNATORY_TYPE_STATIC,
} from "@/utils/pdf_report_signature_helpers";

export default {
  name: "FormalDocumentTemplateSectionEditor",
  components: {
    FormalDocumentTemplateDataTableEditor:
      formal_document_template_data_table_editor_component,
  },
  created() {
    this.ensure_section_shape();
  },
  props: {
    section: {
      type: Object,
      required: true,
    },
    question_options: {
      type: Array,
      default: () => [],
    },
    section_type_options: {
      type: Array,
      default: () => [],
    },
    system_field_options: {
      type: Array,
      default: () => [],
    },
    checklist_field_options: {
      type: Array,
      default: () => [],
    },
    signatory_type_options: {
      type: Array,
      default: () => [],
    },
    section_index: {
      type: Number,
      default: 0,
    },
    total_sections: {
      type: Number,
      default: 0,
    },
  },
  emits: ["open-question-picker", "remove"],
  computed: {
    is_data_table() {
      return this.section.section_type === "data_table";
    },
  },
  watch: {
    "section.section_type": {
      immediate: false,
      handler() {
        this.ensure_section_shape();
      },
    },
  },
  methods: {
    ensure_binding(binding, default_source_type = "system_field") {
      const normalized_binding = ensure_formal_document_binding(
        binding,
        default_source_type
      );

      return normalized_binding;
    },
    ensure_section_shape() {
      if (!this.section?.config) {
        this.section.config = {};
      }
      if (this.section.section_type === "field_list") {
        this.section.config.items = this.get_field_items();
        return;
      }
      if (this.section.section_type === "checklist_matrix") {
        this.section.config.display_fields = this.get_checklist_display_fields();
        this.section.config.rows = this.get_checklist_rows();
        return;
      }
      if (this.section.section_type === "sign_off") {
        this.section.config.signatories = this.get_signatories();
      }
    },
    handle_section_type_change(next_section_type) {
      const next_section = build_section_form(
        next_section_type,
        this.section_index + 1
      );
      this.section.section_type = next_section_type;
      this.section.config = next_section.config;
      if (!this.section.title) {
        this.section.title = next_section.title;
      }
      this.section.is_visible = true;
      this.ensure_section_shape();
    },
    get_field_items() {
      const items = Array.isArray(this.section?.config?.items)
        ? this.section.config.items
        : [];
      if (!items.length) {
        return [build_field_item()];
      }
      return items.map((item) => {
        if (!item || typeof item !== "object") {
          return build_field_item();
        }
        item.binding = this.ensure_binding(item.binding);
        if (item.label === undefined) {
          item.label = "";
        }
        if (!item.local_id) {
          item.local_id = build_field_item().local_id;
        }
        return item;
      });
    },
    add_field_item() {
      this.section.config.items = this.get_field_items();
      this.section.config.items.push(build_field_item());
    },
    handle_field_item_source_selection(item, next_source_type) {
      if (next_source_type === "section_question") {
        this.open_bulk_question_picker("field_items_bulk");
        return;
      }
      item.binding.source_type = next_source_type;
      this.handle_binding_source_change(item.binding, item);
    },
    remove_field_item(local_id) {
      this.section.config.items = this.get_field_items().filter(
        (item) => item.local_id !== local_id
      );
    },
    get_checklist_rows() {
      const rows = Array.isArray(this.section?.config?.rows)
        ? this.section.config.rows
        : [];
      if (!rows.length) {
        return [build_checklist_row()];
      }
      return rows.map((row) => {
        if (!row || typeof row !== "object" || Array.isArray(row)) {
          return build_checklist_row();
        }
        row.binding = this.ensure_binding(row.binding, "section_question");
        if (row.label === undefined) {
          row.label = "";
        }
        if (!row.local_id) {
          row.local_id = build_checklist_row().local_id;
        }
        return row;
      });
    },
    add_checklist_row() {
      this.section.config.rows = this.get_checklist_rows();
      this.section.config.rows.push(build_checklist_row());
    },
    remove_checklist_row(local_id) {
      this.section.config.rows = this.get_checklist_rows().filter(
        (row) => row.local_id !== local_id
      );
    },
    get_signatories() {
      const signatories = Array.isArray(this.section?.config?.signatories)
        ? this.section.config.signatories
        : [];
      if (!signatories.length) {
        return [build_signatory()];
      }
      return signatories.map((signatory) => {
        if (!signatory || typeof signatory !== "object") {
          return build_signatory();
        }
        if (signatory.role === undefined) {
          signatory.role = "";
        }
        if (signatory.signatory_type === undefined) {
          signatory.signatory_type = PDF_SIGNATORY_TYPE_STATIC;
        }
        if (signatory.name === undefined) {
          signatory.name = "";
        }
        if (!signatory.local_id) {
          signatory.local_id = build_signatory().local_id;
        }
        return signatory;
      });
    },
    add_signatory() {
      this.section.config.signatories = this.get_signatories();
      this.section.config.signatories.push(build_signatory());
    },
    remove_signatory(local_id) {
      this.section.config.signatories = this.get_signatories().filter(
        (signatory) => signatory.local_id !== local_id
      );
    },
    handle_signatory_type_change(signatory, next_signatory_type) {
      signatory.signatory_type = next_signatory_type || PDF_SIGNATORY_TYPE_STATIC;
      if (signatory.signatory_type === PDF_SIGNATORY_TYPE_ADMIN) {
        signatory.name = "";
      }
    },
    is_admin_signature_signatory(signatory) {
      return (
        String(signatory?.signatory_type || PDF_SIGNATORY_TYPE_STATIC) ===
        PDF_SIGNATORY_TYPE_ADMIN
      );
    },
    handle_binding_source_change(binding, label_target = null) {
      sync_binding_source(binding);
      return label_target;
    },
    get_checklist_display_fields() {
      const display_fields = this.section?.config?.display_fields;
      if (Array.isArray(display_fields) && display_fields.length) {
        return display_fields;
      }
      return ["evaluation_result"];
    },
    is_checklist_display_field_selected(field_name) {
      return this.get_checklist_display_fields().includes(field_name);
    },
    handle_label_target_change(label_target) {
      return label_target;
    },
    toggle_checklist_display_field(field_name, is_checked) {
      const next_fields = is_checked
        ? [...this.get_checklist_display_fields(), field_name]
        : this.get_checklist_display_fields().filter(
            (selected_field) => selected_field !== field_name
          );
      this.section.config.display_fields = next_fields.length
        ? Array.from(new Set(next_fields))
        : ["evaluation_result"];
    },
    get_question_label(section_question_id) {
      return (
        find_question_option(this.question_options, section_question_id)?.label ||
        "No question selected"
      );
    },
    get_label_placeholder(target_row) {
      return build_placeholder_label_from_binding(
        target_row?.binding,
        this.question_options
      );
    },
    open_question_picker(target_type, target_local_id, selection_mode = "single") {
      const selected_question_ids = this.get_selected_question_ids(
        target_type,
        target_local_id
      );
      this.$emit("open-question-picker", {
        section_local_id: this.section.local_id,
        selection_mode,
        selected_question_ids,
        target_local_id,
        target_type,
      });
    },
    get_selected_question_ids(target_type, target_local_id) {
      if (target_type === "row_source_questions") {
        return [...(this.section.config.row_source?.question_ids || [])];
      }
      const binding = this.find_target_binding(target_type, target_local_id);
      return binding?.section_question_id ? [binding.section_question_id] : [];
    },
    find_target_binding(target_type, target_local_id) {
      const collection_name = this.get_collection_name(target_type);
      const items = Reflect.get(this.section.config, collection_name) || [];
      return items.find((item) => item.local_id === target_local_id)?.binding || null;
    },
    get_collection_name(target_type) {
      if (target_type === "field_item") {
        return "items";
      }
      if (target_type === "checklist_row") {
        return "rows";
      }
      return "columns";
    },
    open_bulk_question_picker(target_type) {
      this.$emit("open-question-picker", {
        section_local_id: this.section.local_id,
        selection_mode: "multiple",
        selected_question_ids: this.get_bulk_selected_question_ids(target_type),
        target_local_id: target_type,
        target_type,
      });
    },
    get_bulk_selected_question_ids(target_type) {
      if (target_type === "field_items_bulk") {
        return get_bound_question_ids(this.get_field_items());
      }
      if (target_type === "checklist_rows_bulk") {
        return get_bound_question_ids(this.get_checklist_rows());
      }
      return [];
    },
    get_bulk_question_count(target_type) {
      return this.get_bulk_selected_question_ids(target_type).length;
    },
    get_checklist_field_description(field_name) {
      if (field_name === "evaluation_result") {
        return "Adds the Yes, No, and N/A result columns to the PDF.";
      }
      if (field_name === "answered") {
        return "Shows whether the linked question has any answer captured.";
      }
      if (field_name === "verified") {
        return "Shows whether the linked answer was verified.";
      }
      return "Shows any saved comments linked to the answer or document.";
    },
  },
};
